import { useState, useEffect } from 'react'
import { Modal, Button, Input, Textarea, Flex, Text, Badge, Select, Box, Stack, Group, Divider, Alert } from '@mantine/core';
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs from 'dayjs';
import { renovation_customer } from '../../utils/apis/home'
import { get_banks } from '../../utils/apis/banks'
import { HiOutlineCheckCircle, HiOutlineClock, HiOutlineCurrencyDollar, HiOutlineLibrary } from 'react-icons/hi';

const ModalRenovation = ({ onClose, isOpen, item }) =>
{
  const [ loading, setLoading ] = useState(false)
  const [ banks, setBanks ] = useState([])
  const [ showConfirm, setShowConfirm ] = useState(false)

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: Yup.object(validationSchema()),
    onSubmit: async (data) =>
    {
      setLoading(true)
      try {
        const record = {
          _id: item._id,
          num_account: data.num_account,
          comments: data.comments,
          date: dayjs(data.date).format('DD/MM/YYYY'),
          amount: data.amount,
          number_renovation: Number(data.numero),
          type_renovation: data.type_renovation,
          type_bank_name: data.type_bank_name
        }
        await renovation_customer(record);
        onClose(true)
      } catch (error) {
        console.error('Error in renovation:', error);
      } finally {
        setLoading(false)
      }
    }
  })

  const close = () =>
  {
    formik.resetForm()
    setShowConfirm(false)
    onClose(null)
  }

  useEffect(() =>
  {
    if (item && isOpen) {
      getBanks()
      formik.setFieldValue('num_account', item.num_account);
      if (item.type_contract === 'Postpago') {
        formik.setFieldValue('date', dayjs(item.date_billing).format('YYYY-MM-DD'));
      } else {
        formik.setFieldValue('date', dayjs().format('YYYY-MM-DD'));
      }
      formik.setFieldValue('numero', "1");
      formik.setFieldValue('type_renovation', 'MONTH');
      formik.setFieldValue('amount', item.price_type_plan || item.price);
    }
  }, [ item, isOpen ])

  const getBanks = async () =>
  {
    try {
      const res = await get_banks();
      setBanks(res || [])
    } catch (error) {
      console.error(error)
    }
  }

  const nextExpiry = dayjs(formik.values.date).add(Number(formik.values.numero || 0), formik.values.type_renovation === 'MONTH' ? 'month' : formik.values.type_renovation === 'DAY' ? 'day' : 'year');

  return (
    <>
      <Modal 
        size="lg" 
        opened={ isOpen } 
        onClose={ close } 
        centered 
        radius="lg"
        title={
          <Group gap="xs">
            <Text fw={700} size="lg">Renovación de Servicio</Text>
            <Badge variant="light" color="yellow" radius="sm">{item?.num_account}</Badge>
          </Group>
        }
      >
        { showConfirm ? (
          <Stack p="xs" gap="md">
            <Alert icon={<HiOutlineCheckCircle size={20} />} color="yellow" title="Verifica los datos antes de continuar" radius="md">
              Estás a punto de registrar un pago y extender la vigencia del servicio.
            </Alert>

            <Box className="bg-slate-50 p-md rounded-lg border border-slate-200">
              <Stack gap="xs">
                <Flex justify="space-between">
                  <Text size="sm" c="dimmed" fw={600}>MONTO A REGISTRAR:</Text>
                  <Text size="md" fw={800} c="yellow.9" className="font-mono">$ {Number(formik.values.amount).toFixed(2)} MXN</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="sm" c="dimmed" fw={600}>PERIODO:</Text>
                  <Text size="sm" fw={700}>{formik.values.numero} {formik.values.type_renovation === 'MONTH' ? 'MES(ES)' : formik.values.type_renovation === 'DAY' ? 'DÍA(S)' : 'AÑO(S)'}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text size="sm" c="dimmed" fw={600}>ENTIDAD BANCARIA:</Text>
                  <Text size="sm" fw={700} tt="uppercase">{formik.values.type_bank_name}</Text>
                </Flex>
                <Divider my="xs" />
                <Flex justify="space-between" align="center">
                  <Text size="sm" fw={800}>NUEVA VIGENCIA HASTA:</Text>
                  <Badge size="lg" color="green" variant="filled" radius="sm">
                    {nextExpiry.format('DD/MMM/YYYY').toUpperCase()}
                  </Badge>
                </Flex>
              </Stack>
            </Box>

            <Group grow mt="md">
              <Button variant='light' color='gray' onClick={ () => setShowConfirm(false) }>
                Corregir Datos
              </Button>
              <Button loading={loading} onClick={ () => formik.handleSubmit() }>
                Confirmar y Renovar
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md" p="xs">
            <Box className="grid grid-cols-2 gap-md">
              <Input.Wrapper label="Número de Cuenta">
                <Input
                  variant="filled"
                  radius="md"
                  readOnly
                  value={ formik.values.num_account }
                />
              </Input.Wrapper>
              <Input.Wrapper label="Monto del Pago">
                <Input
                  variant="filled"
                  radius="md"
                  leftSection={<HiOutlineCurrencyDollar />}
                  placeholder="0.00"
                  type="number"
                  {...formik.getFieldProps('amount')}
                  error={formik.touched.amount && formik.errors.amount}
                />
              </Input.Wrapper>
            </Box>

            <Box className="grid grid-cols-2 gap-md">
              <Input.Wrapper label="Vigencia Actual / Inicio">
                <Input
                  type="date"
                  variant="filled"
                  radius="md"
                  leftSection={<HiOutlineClock />}
                  {...formik.getFieldProps('date')}
                  error={formik.touched.date && formik.errors.date}
                />
              </Input.Wrapper>
              <Input.Wrapper label="Banco Receptor">
                <Select 
                  placeholder='Selecciona Banco'
                  variant="filled"
                  radius="md"
                  leftSection={<HiOutlineLibrary />}
                  data={ banks.map(b => b.name) }
                  value={formik.values.type_bank_name}
                  onChange={(val) => formik.setFieldValue('type_bank_name', val)}
                  error={formik.touched.type_bank_name && formik.errors.type_bank_name}
                />
              </Input.Wrapper>
            </Box>

            <Box>
              <Text size="sm" fw={500} mb={4}>Extender Periodo por:</Text>
              <Group grow gap="xs">
                <Select 
                  placeholder='Cant.'
                  variant="filled"
                  radius="md"
                  w={80}
                  data={ ["1", "2", "3", "6", "12"] }
                  value={formik.values.numero}
                  onChange={(val) => formik.setFieldValue('numero', val)}
                />
                <Button.Group>
                  <Button 
                    variant={formik.values.type_renovation === 'DAY' ? 'filled' : 'light'} 
                    size="xs" 
                    onClick={() => formik.setFieldValue('type_renovation', 'DAY')}
                  >Días</Button>
                  <Button 
                    variant={formik.values.type_renovation === 'MONTH' ? 'filled' : 'light'} 
                    size="xs"
                    onClick={() => formik.setFieldValue('type_renovation', 'MONTH')}
                  >Meses</Button>
                  <Button 
                    variant={formik.values.type_renovation === 'YEAR' ? 'filled' : 'light'} 
                    size="xs"
                    onClick={() => formik.setFieldValue('type_renovation', 'YEAR')}
                  >Años</Button>
                </Button.Group>
              </Group>
            </Box>

            <Input.Wrapper label="Comentarios / Referencia">
              <Textarea
                variant="filled"
                radius="md"
                placeholder="Detalles adicionales del pago..."
                minRows={2}
                {...formik.getFieldProps('comments')}
              />
            </Input.Wrapper>

            <Group grow mt="lg">
              <Button variant='subtle' color='gray' onClick={ close }>
                Cancelar
              </Button>
              <Button 
                disabled={!formik.values.type_bank_name} 
                onClick={ () => setShowConfirm(true) }
              >
                Revisar Renovación
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  )
}

export default ModalRenovation;

function initialValues ()
{
  return {
    num_account: '',
    comments: '',
    date: '',
    amount: '',
    numero: '1',
    type_renovation: 'MONTH',
    type_bank_name: ''
  }
}

function validationSchema ()
{
  return {
    num_account: Yup.string().required('Requerido'),
    date: Yup.string().required('Requerido'),
    amount: Yup.number().positive('Debe ser mayor a 0').required('Requerido'),
    numero: Yup.number().required('Requerido'),
    type_renovation: Yup.string().required('Requerido'),
    type_bank_name: Yup.string().required('Selecciona un banco')
  }
}
